/**
 * apps/command session state.
 *
 * AUTHORIZATION IS CAPABILITIES, NOT RANK — the same rule the murojaat panel
 * follows (D-032 §4.0), and it matters more here, not less: `admin_role_t` now
 * carries ten values, and any comparison by position would give an MBT role
 * whatever the enum happens to declare before it.
 *
 * `can()` from `@navoiy/domain` is the ONLY thing this app may branch on for
 * permission. `role` is read for exactly one non-permission purpose — which
 * home §15.7 opens on — and that is a routing decision, not an access one.
 *
 * The server enforces every gate independently. These checks exist so an
 * official is not shown a control that can only 403.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { can, type AdminCapability } from '@navoiy/domain';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';

export type AdminMe = components['schemas']['AdminMe'];

type Status = 'loading' | 'authenticated' | 'anonymous';

interface SessionContextValue {
  status: Status;
  me: AdminMe | null;
  /** Capability gate — never compare `role` directly. */
  allows: (capability: AdminCapability) => boolean;
  login: (email: string, password: string, totp?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Called when any request 401s, so the panel returns to the login screen. */
  expire: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMe] = useState<AdminMe | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = unwrap(await api().GET('/admin/me'));
        if (!cancelled) {
          setMe(data);
          setStatus('authenticated');
        }
      } catch {
        // A 401 here is the normal cold start, not an error worth surfacing.
        if (!cancelled) {
          setMe(null);
          setStatus('anonymous');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string, totp?: string) => {
    // `totp` is OMITTED rather than sent empty. `AdminLoginRequest.totp` keeps
    // its `^[0-9]{6}$` pattern whenever the field is present, so `totp: ''` is a
    // 422 from the contract validator — a louder and differently-shaped failure
    // than the 401 the login screen is built around, and it would hit every
    // password-only account (migration 0018) on every sign-in.
    const code = totp?.trim();
    const body = { email, password, ...(code ? { totp: code } : {}) };
    const data = unwrap(await api().POST('/admin/auth/login', { body }));
    setMe(data);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api().POST('/admin/auth/logout');
    } catch (error) {
      // A failed logout must still clear the client. The cookie may already be
      // gone, and leaving an operator looking at a console they can no longer
      // query is worse than a redundant sign-out.
      if (!(error instanceof ApiError)) throw error;
    }
    setMe(null);
    setStatus('anonymous');
  }, []);

  const expire = useCallback(() => {
    setMe(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      me,
      allows: (capability) => (me ? can(me.role, capability) : false),
      login,
      logout,
      expire,
    }),
    [status, me, login, logout, expire],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside SessionProvider');
  return value;
}
